// The ten terminal palettes, lifted verbatim from the console mockup
// (VF Terminal Console.dc.html, `build()` -> this.THEMES). Nord is the default.
//
// These are the single source of truth: theme.ts hands the active palette to
// Mantine via `theme.other.palette`, and cssVariablesResolver.ts emits it as
// --vf-* custom properties AND maps Mantine's own variables onto them, so every
// stock Mantine component inherits the terminal look without per-usage styles.

export interface Palette {
  /** Display label shown in the settings selector. */
  name: string;
  bg: string;
  panel: string;
  panel2: string;
  border: string;
  border2: string;
  hover: string;
  text: string;
  dim: string;
  dim2: string;
  sec: string;
  acc: string;
  acc2: string;
  accbg: string;
  ok: string;
  okbd: string;
  okbg: string;
  warn: string;
  bad: string;
  badbg: string;
  badbd: string;
  /** Colour of the CRT scanline overlay. */
  scan: string;
}

export const PALETTES = {
  matrix: {
    name: "matrix",
    bg: "#060a06", panel: "#0a120a", panel2: "#050805", border: "#17301a", border2: "#0e1c10",
    hover: "#0c1a0e", text: "#b8f0c0", dim: "#4a7a52", dim2: "#2f4a36", sec: "#6aa876",
    acc: "#39ff8a", acc2: "#7dffb0", accbg: "#0e2416", ok: "#39ff8a", okbd: "#1c3a24",
    okbg: "#0e2416", warn: "#eaff6a", bad: "#ff5f6b", badbg: "#2a0e10", badbd: "#4a1c22",
    scan: "rgba(57,255,138,.03)",
  },
  ice: {
    name: "ice",
    bg: "#05080f", panel: "#0a111c", panel2: "#060b14", border: "#16273c", border2: "#0e1826",
    hover: "#0d1826", text: "#cfe3f7", dim: "#5a7691", dim2: "#3a506a", sec: "#7f9cba",
    acc: "#4fc3ff", acc2: "#86d6ff", accbg: "#0e2233", ok: "#57e6c3", okbd: "#16323a",
    okbg: "#0e2226", warn: "#ffcf5a", bad: "#ff6b8a", badbg: "#260e16", badbd: "#451c2a",
    scan: "rgba(79,195,255,.03)",
  },
  magenta: {
    name: "magenta",
    bg: "#0b060d", panel: "#150a18", panel2: "#0a0510", border: "#331a3a", border2: "#1c0e20",
    hover: "#180a1c", text: "#f0d6f2", dim: "#8a5f92", dim2: "#5a3a60", sec: "#b07fb8",
    acc: "#ff5ce1", acc2: "#ff92ec", accbg: "#24102a", ok: "#7dffcf", okbd: "#1c3a30",
    okbg: "#0e2420", warn: "#ffd15a", bad: "#ff5f7a", badbg: "#2a0e18", badbd: "#4a1c2c",
    scan: "rgba(255,92,225,.03)",
  },
  nord: {
    name: "nord",
    bg: "#20242e", panel: "#2a2f3b", panel2: "#1b1f28", border: "#3b4252", border2: "#2e3440",
    hover: "#2e3440", text: "#e5e9f0", dim: "#7a869c", dim2: "#4c566a", sec: "#a3adc0",
    acc: "#88c0d0", acc2: "#a6d4e2", accbg: "#2e3a44", ok: "#a3be8c", okbd: "#3a4a3a",
    okbg: "#2a352a", warn: "#ebcb8b", bad: "#bf616a", badbg: "#3a2a2c", badbd: "#5a3a40",
    scan: "rgba(136,192,208,.025)",
  },
  grape: {
    name: "grape",
    bg: "#0c0816", panel: "#140e22", panel2: "#0a0714", border: "#2a2044", border2: "#171029",
    hover: "#171029", text: "#ded2f4", dim: "#7a6aa2", dim2: "#4e3f70", sec: "#a493c8",
    acc: "#9d7bff", acc2: "#b9a0ff", accbg: "#1e1638", ok: "#6ee7c8", okbd: "#1c3a34",
    okbg: "#0e2420", warn: "#ffce6a", bad: "#ff6f8f", badbg: "#260e1a", badbd: "#45203a",
    scan: "rgba(157,123,255,.03)",
  },
  crimson: {
    name: "crimson",
    bg: "#0d0707", panel: "#170a0a", panel2: "#0c0505", border: "#3a1c1c", border2: "#200e0e",
    hover: "#1a0c0c", text: "#f0d6d2", dim: "#9a6a64", dim2: "#603c3a", sec: "#b88880",
    acc: "#ff5a52", acc2: "#ff8a80", accbg: "#2a1010", ok: "#7de08a", okbd: "#1c3a24",
    okbg: "#0e2414", warn: "#ffb45a", bad: "#ff5f6b", badbg: "#2a0e0e", badbd: "#4a1c1c",
    scan: "rgba(255,90,82,.03)",
  },
  mono: {
    name: "mono",
    bg: "#0a0a0a", panel: "#131313", panel2: "#080808", border: "#2a2a2a", border2: "#181818",
    hover: "#161616", text: "#e0e0e0", dim: "#767676", dim2: "#454545", sec: "#9a9a9a",
    acc: "#ffffff", acc2: "#d0d0d0", accbg: "#1e1e1e", ok: "#c8c8c8", okbd: "#333333",
    okbg: "#161616", warn: "#a0a0a0", bad: "#ff7676", badbg: "#241414", badbd: "#443333",
    scan: "rgba(255,255,255,.02)",
  },
  goldteal: {
    name: "gold·teal",
    bg: "#04100e", panel: "#0a1a17", panel2: "#050d0b", border: "#163430", border2: "#0e201c",
    hover: "#0c201c", text: "#d6f0e8", dim: "#5a8a7e", dim2: "#3a5a52", sec: "#7fb0a4",
    acc: "#ffd45a", acc2: "#ffe28a", accbg: "#123028", ok: "#4fe0b0", okbd: "#163a30",
    okbg: "#0e2420", warn: "#ffb45a", bad: "#ff6b7a", badbg: "#260e14", badbd: "#451c28",
    scan: "rgba(255,212,90,.03)",
  },
  cyberpunk: {
    name: "cyberpunk",
    bg: "#0a0a12", panel: "#12121e", panel2: "#080810", border: "#26264a", border2: "#16162a",
    hover: "#16162a", text: "#e6e6ff", dim: "#6a6aa0", dim2: "#40406a", sec: "#9494c8",
    acc: "#f9f002", acc2: "#fff85a", accbg: "#1e1e2e", ok: "#05ffa1", okbd: "#163a30",
    okbg: "#0e2420", warn: "#ff9f2e", bad: "#ff2e6a", badbg: "#2a0e18", badbd: "#4a1c2c",
    scan: "rgba(249,240,2,.025)",
  },
  royal: {
    name: "royal",
    bg: "#060814", panel: "#0c1024", panel2: "#060814", border: "#1c2650", border2: "#111838",
    hover: "#111838", text: "#d6def7", dim: "#5f6fa2", dim2: "#3a4670", sec: "#8a97c8",
    acc: "#5b7bff", acc2: "#8aa0ff", accbg: "#141e40", ok: "#5be0b0", okbd: "#163a34",
    okbg: "#0e2420", warn: "#ffce6a", bad: "#ff6b8a", badbg: "#0e1638", badbd: "#2a3a6a",
    scan: "rgba(91,123,255,.03)",
  },
} satisfies Record<string, Palette>;

export type PaletteKey = keyof typeof PALETTES;

export const PALETTE_KEYS = Object.keys(PALETTES) as PaletteKey[];

export const DEFAULT_PALETTE: PaletteKey = "nord";

export function paletteFor(key: string | null | undefined): Palette {
  return PALETTES[(key ?? "") as PaletteKey] ?? PALETTES[DEFAULT_PALETTE];
}

export type Density = "comfortable" | "compact";
